import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { taxApi, type CreateTaxData, type TaxTypeChoice } from '@/lib/api';

interface AddTaxModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  onTaxCreated: (taxId: string) => void;
}

export function AddTaxModal({ open, onClose, businessId, onTaxCreated }: AddTaxModalProps) {
  const [taxTypeChoices, setTaxTypeChoices] = useState<TaxTypeChoice[]>([]);
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [taxType, setTaxType] = useState('exclusive');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    taxApi.getTaxTypes().then((response) => {
      if (response.data) {
        setTaxTypeChoices(response.data);
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setSubmitting(true);

    const data: CreateTaxData = {
      name,
      rate,
      tax_type: taxType,
      apply_to_all_parts: false,
      apply_to_all_labor: false,
      business: businessId,
    };

    const response = await taxApi.create(data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      // Reset form
      setName('');
      setRate('');
      setTaxType('exclusive');
      setError('');

      // Notify parent and close
      onTaxCreated(response.data.id);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Tax</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="modal-name">Tax Name *</Label>
            <Input
              id="modal-name"
              placeholder="e.g., Sales Tax, VAT"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="modal-rate">Tax Rate (%) *</Label>
            <Input
              id="modal-rate"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="e.g., 8.25"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="modal-taxType">Tax Type</Label>
            <Select value={taxType} onValueChange={setTaxType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {taxTypeChoices.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              <strong>Exclusive:</strong> Added on top | <strong>Inclusive:</strong> Included in price
            </p>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={submitting || !name || !rate}>
              {submitting ? 'Creating...' : 'Create Tax'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
